import React, { useEffect, useRef, useState } from 'react'
import { Dropdown } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import MoreVertIcon from '@mui/icons-material/MoreVert'

import { getKmsVersionAsync } from '../../../api/kmsVersionApi'
import { IRootState } from '../../../redux/reducers'
import { ConfigurationState } from '../../../redux/reducers/initialState'

/**
 * Array of link objects to be displayed in the dropdown menu.
 * Each link object contains the text to be displayed and the href URL.
 */
export const links = [
  {
    text: 'faq',
    href: 'https://support.hpwolf.com/s/article/KMS-and-HP-Sure-Admin-FAQ',
  },
  {
    text: 'release_history',
    href: 'https://support.hpwolf.com/s/article/HP-Sure-Admin-Release-History',
  },
]

/**
 * InfoMenu component displays a dropdown menu with links and version information.
 *
 * @returns {JSX.Element} The InfoMenu component.
 */
export const InfoMenu: React.FC = () => {
  const configuration: ConfigurationState | null = useSelector(
    (state: IRootState) => state.configuration,
  )
  const [version, setVersion] = useState('unknown')
  const { t } = useTranslation()
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    const fetchVersion = async () => {
      if (configuration !== null) {
        try {
          const kmsVersion = await getKmsVersionAsync()
          if (kmsVersion) {
            const versionParts = kmsVersion.split('.')
            const version = versionParts.slice(0, 3).join('.')
            setVersion(version)
          }
        } catch (error) {
          toast.error(t('version_fetch_failed'))
        }
      }
    }

    fetchVersion()
  }, [configuration])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [dropdownRef])

  return (
    <Dropdown
      align="end"
      style={{ marginRight: '10px' }}
      show={isOpen}
      ref={dropdownRef}
    >
      <Dropdown.Toggle
        as={({ children, onClick }: any) => (
          <button
            onClick={(e) => {
              e.preventDefault()
              onClick(e)
            }}
            style={{
              ...customDropdownToggle,
              background: 'none',
              border: 'none',
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.1)')
            }
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = ''
              e.currentTarget.style.background = 'none'
            }}
            onMouseUp={() => setIsOpen(!isOpen)}
          >
            {children}
          </button>
        )}
        id="dropdown-custom-components"
      >
        <MoreVertIcon data-testid="MoreVertIcon" style={{ color: 'black' }} />
      </Dropdown.Toggle>
      <Dropdown.Menu flip={false} style={dropdownMenuStyle}>
        {links.map((link, index) => (
          <Dropdown.Item
            key={index}
            style={dropdownMenuItemStyle}
            href={link.href}
            target="_blank"
          >
            <div style={{ padding: '0px 4px 0px 4px' }}>{t(link.text)}</div>
          </Dropdown.Item>
        ))}
        <Dropdown.Divider style={dropdownDividerStyle} />
        <Dropdown.Item
          as="div"
          style={{
            ...dropdownMenuItemStyle,
            ...dropdownMenuItemNoHoverStyle,
            color: '#A3A3A3',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <InfoOutlinedIcon style={{ marginRight: '3px' }} />
          {t('version')}: {version === 'unknown' ? t('unknown') : version}
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  )
}

const customDropdownToggle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  transition: 'background-color 0.3s',
}

const dropdownMenuStyle: React.CSSProperties = {
  marginTop: '10px',
  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
  borderRadius: '15px',
  border: 'none',
}

const dropdownMenuItemStyle: React.CSSProperties = {
  fontSize: 'inherit',
  width: 'calc(100% - 20px)',
  height: '100%',
  padding: '8px 12px',
  margin: '2px 10px',
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  borderRadius: '10px',
}

const dropdownMenuItemNoHoverStyle: React.CSSProperties = {
  backgroundColor: 'inherit !important',
  color: 'inherit !important',
}

const dropdownDividerStyle: React.CSSProperties = {
  marginInline: '10px',
}
